import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Dialog,
    Slide,
    AppBar,
    Toolbar,
    IconButton,
    Typography,
    Box,
    Button,
    Autocomplete,
    TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Langs from "../../../app/lang/langs";
import { showErrorToast } from "../../../app/utils/helpers";
import { getCollabsNames } from "../../../components/vcomp/vcompSlice";
import { addCollabMission, editCollabMission, ctGetCollabMissions } from "../../../components/teams/teamsSlice";
import { getTeamCollabs } from "../../../components/clients/clientsSlice";
import { getPostes } from "../../../components/collabs/collabsSlice";
import { SelectInput } from "../../../components/inputs/Select";
import { confirmDialog } from "../../../components/popups/ConfirmDialog";
import moment from "moment";

/* ------------------------------------------------------------------ */
/*  Shared style constants                                             */
/* ------------------------------------------------------------------ */
const TRANSITION_SMOOTH = "all 0.28s cubic-bezier(0.4, 0, 0.2, 1)";
const BORDER_RADIUS_INPUT = "14px";

const fieldLabelSx = {
    fontWeight: 700,
    mb: 0.75,
    letterSpacing: "0.04em",
    color: "rgba(0,0,0,0.45)",
    fontSize: "0.72rem",
    textTransform: "uppercase",
};

const glassmorphicTextFieldSx = {
    mb: 2,
    "& .MuiOutlinedInput-root": {
        borderRadius: BORDER_RADIUS_INPUT,
        backgroundColor: "rgba(255,255,255,0.5)",
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.8)",
        transition: TRANSITION_SMOOTH,
        "& fieldset": {
            borderColor: "rgba(255,144,21,0.08)",
            transition: TRANSITION_SMOOTH,
        },
        "&:hover fieldset": {
            borderColor: "rgba(255,144,21,0.2)",
        },
        "&.Mui-focused fieldset": {
            borderColor: "rgba(255,144,21,0.5)",
            boxShadow: "0 0 0 3px rgba(255,144,21,0.1)",
        },
        "&.Mui-focused": {
            backgroundColor: "rgba(255,255,255,0.10)",
        },
    },
    "& input": {
        fontSize: "16px",
        transition: TRANSITION_SMOOTH,
    },
    "& input::placeholder": {
        opacity: 0.45,
    },
};

const glassCardSx = {
    background: "rgba(255,255,255,0.65)",
    backdropFilter: "blur(20px) saturate(1.3)",
    WebkitBackdropFilter: "blur(20px) saturate(1.3)",
    borderRadius: "20px",
    border: "1px solid rgba(255,255,255,0.5)",
    boxShadow: "0 4px 24px rgba(0,0,0,0.04), 0 1px 4px rgba(0,0,0,0.02), inset 0 1px 0 rgba(255,255,255,0.7)",
    p: 2.5,
    mb: 2,
    transition: TRANSITION_SMOOTH,
};

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

export default function MobileAddTeamsMembersModal(props) {
    const dispatch = useDispatch();

    const collabsNames = useSelector((state) => state.vcomp.collabsNames);
    const postes = useSelector((state) => state.collabs.postes);
    const collabMissions = useSelector((state) => state.teams.collabMissions);

    const [filteredCollabs, setFilteredCollabs] = useState([]);
    const [associatedMembers, setAssociatedMembers] = useState(null);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [onGoingMission, setOnGoingMission] = useState(null);
    const [newPoste, setNewPoste] = useState();
    const [postesOptions, setPostesOptions] = useState([]);
    const [disabedDatesMissions, setDisabedDatesMissions] = useState([]);

    useEffect(() => {
        const options = postes?.map((poste) => ({
            label: poste.name,
            value: poste.id,
        }));
        setPostesOptions(options);
    }, [postes]);

    useEffect(() => {
        if (associatedMembers && associatedMembers.id) {
            dispatch(ctGetCollabMissions({ id_collab: associatedMembers.id }));
        }
    }, [associatedMembers]);

    useEffect(() => {
        if (collabMissions && collabMissions.ongoingMissions) {
            setOnGoingMission(collabMissions.ongoingMissions[0] || null);
        }
    }, [collabMissions]);

    useEffect(() => {
        if (collabMissions) {
            const allMissions = [
                ...collabMissions.ongoingMissions,
                ...collabMissions.upcomingMissions,
                ...collabMissions.pastMissions,
            ];

            const newDisabledDates = [];
            allMissions.forEach((mission) => {
                let start = mission.start_date ? new Date(mission.start_date) : null;
                let end = mission.end_date ? new Date(mission.end_date) : null;

                if (!start && !end) {
                    return;
                } else if (!start) {
                    return;
                } else if (start && !end) {
                    end = moment().add(30, "years").toDate();
                }

                let current = new Date(start);
                while (current <= end) {
                    newDisabledDates.push(new Date(current));
                    current.setDate(current.getDate() + 1);
                }
            });

            setDisabedDatesMissions(newDisabledDates);
        }
    }, [collabMissions]);

    useEffect(() => {
        dispatch(getCollabsNames());
        dispatch(getPostes());

        setFilteredCollabs(
            collabsNames
                .filter((collab) => !props.prevCollabs.includes(collab.id))
                .map(({ id, nom, prenom }) => ({
                    id,
                    fullName: `${nom} ${prenom}`,
                }))
        );
    }, [props.idTeam, props.prevCollabs]);

    const handleClose = () => {
        props.setVisible(false);
        setAssociatedMembers(null);
        setFilteredCollabs([]);
        setStartDate(new Date());
        setEndDate(null);
        setNewPoste(null);
    };

    const handleAddMembers = () => {
        if (associatedMembers && associatedMembers.id && props.idTeam) {
            if (startDate && endDate && startDate > endDate) {
                showErrorToast("La date de début doit être antérieure ou égale à la date de fin.");
                return;
            }
            if (!newPoste) {
                showErrorToast("Veuillez choisir un poste.");
                return;
            }

            dispatch(
                addCollabMission({
                    id_team: props.idTeam,
                    id_collab: associatedMembers.id,
                    start_date: moment(startDate).format("YYYY-MM-DD 00:00:00"),
                    end_date: endDate ? moment(endDate).format("YYYY-MM-DD 23:59:59") : null,
                    id_poste: newPoste,
                })
            )
                .then((actionResponse) => {
                    if (addCollabMission.fulfilled.match(actionResponse)) {
                        if (
                            actionResponse.payload.responseData &&
                            actionResponse.payload.responseData.status
                        ) {
                            dispatch(getTeamCollabs({ id_team: props.idTeam }));
                            handleClose();
                        } else {
                            if (
                                actionResponse.payload.responseData.error &&
                                actionResponse.payload.responseData.error.includes("conflict")
                            ) {
                                showErrorToast("La mission est en conflit avec une autre");
                            }
                        }
                    }
                })
                .catch((error) => {
                    console.error(error);
                    console.error("Échec de l'ajout/suppression des membres");
                });
        }
    };

    const confirmAddWithConflict = (idCollab, teamName, idTeam, startDate, endDate) => {
        const accept = () => {
            dispatch(
                editCollabMission({
                    id_collab: idCollab,
                    end_date: startDate,
                    id_mission: onGoingMission.id_mission,
                })
            ).then((actionResponse) => {
                if (editCollabMission.fulfilled.match(actionResponse)) {
                    if (
                        actionResponse.payload.responseData &&
                        actionResponse.payload.responseData.status
                    ) {
                        dispatch(
                            addCollabMission({
                                id_team: props.idTeam,
                                id_collab: idCollab,
                                start_date: startDate,
                                end_date: endDate,
                                id_poste: newPoste,
                            })
                        ).then((actionResponse) => {
                            if (addCollabMission.fulfilled.match(actionResponse)) {
                                if (
                                    actionResponse.payload.responseData &&
                                    actionResponse.payload.responseData.status
                                ) {
                                    dispatch(getTeamCollabs({ id_team: props.idTeam }));
                                    handleClose();
                                }
                            }
                        });
                    }
                }
            });
        };
        confirmDialog({
            message: `${teamName}`,
            confirmLabel: <Langs str="YES" />,
            cancelLabel: <Langs str="NO" />,
            onConfirm: accept,
        });
    };

    /* Helper: format date for native input */
    const formatDateForInput = (date) => {
        if (!date) return "";
        return moment(date).format("YYYY-MM-DD");
    };

    /* Helper: parse native date input */
    const parseDateInput = (value) => {
        if (!value) return null;
        return new Date(value + "T00:00:00");
    };

    return (
        <Dialog
            fullScreen
            open={props.visible}
            onClose={handleClose}
            TransitionComponent={Transition}
            sx={{ zIndex: 1500 }}
            PaperProps={{
                sx: {
                    background: "linear-gradient(180deg, #f8f9fb 0%, #f0f1f5 100%)",
                },
            }}
        >
            {/* ── Sticky AppBar ── */}
            <AppBar
                sx={{
                    position: "sticky",
                    top: 0,
                    background: "rgba(255,255,255,0.82)",
                    backdropFilter: "blur(24px) saturate(1.5)",
                    WebkitBackdropFilter: "blur(24px) saturate(1.5)",
                    boxShadow: "0 1px 12px rgba(0,0,0,0.06)",
                    borderBottom: "1px solid rgba(0,0,0,0.06)",
                    color: "text.primary",
                    zIndex: 10,
                    "&::after": {
                        content: '""',
                        position: "absolute",
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: "2px",
                        background: "linear-gradient(90deg, transparent, rgba(255,144,21,0.3), transparent)",
                    },
                }}
            >
                <Toolbar sx={{ minHeight: 56, px: 1 }}>
                    <IconButton
                        edge="start"
                        onClick={handleClose}
                        sx={{
                            mr: 1,
                            color: "text.secondary",
                            "&:active": { transform: "scale(0.92)" },
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                    <Typography
                        variant="h6"
                        sx={{
                            flex: 1,
                            fontWeight: 700,
                            fontSize: "1.05rem",
                            letterSpacing: "-0.02em",
                        }}
                    >
                        <Langs str="ADD_MEMBERS" />
                    </Typography>
                </Toolbar>
            </AppBar>

            {/* ── Body ── */}
            <Box sx={{ px: 2, pt: 2.5, pb: 14, mt: 0 }}>
                {/* Collaborateur selection */}
                <Box sx={glassCardSx}>
                    <Typography sx={fieldLabelSx}>
                        <Langs str="Associated Members" />
                    </Typography>
                    <Autocomplete
                        options={filteredCollabs}
                        getOptionLabel={(option) => option.fullName || ""}
                        value={associatedMembers}
                        onChange={(_, newVal) => setAssociatedMembers(newVal)}
                        isOptionEqualToValue={(option, value) => option.id === value?.id}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder="Sélectionner un membre..."
                                sx={glassmorphicTextFieldSx}
                                size="small"
                            />
                        )}
                        componentsProps={{
                            popper: { sx: { zIndex: 1600 } },
                        }}
                        noOptionsText="Aucun résultat"
                        sx={{ mb: 0 }}
                    />
                </Box>

                {/* Poste selection */}
                <Box sx={glassCardSx}>
                    <Typography sx={fieldLabelSx}>
                        <Langs str="POSTE" />
                    </Typography>
                    <SelectInput
                        id="poste_name"
                        options={postesOptions}
                        value={newPoste}
                        onChange={(e) => setNewPoste(e.value)}
                        placeholder="Nom du poste"
                    />
                </Box>

                {/* Start Date */}
                <Box sx={glassCardSx}>
                    <Typography sx={fieldLabelSx}>
                        <Langs str="START_DATE" />
                    </Typography>
                    <TextField
                        type="date"
                        value={formatDateForInput(startDate)}
                        onChange={(e) => setStartDate(parseDateInput(e.target.value))}
                        fullWidth
                        size="small"
                        sx={glassmorphicTextFieldSx}
                        InputLabelProps={{ shrink: true }}
                    />
                </Box>

                {/* End Date */}
                <Box sx={glassCardSx}>
                    <Typography sx={fieldLabelSx}>
                        <Langs str="END_DATE" />
                    </Typography>
                    <TextField
                        type="date"
                        value={formatDateForInput(endDate)}
                        onChange={(e) => setEndDate(parseDateInput(e.target.value))}
                        fullWidth
                        size="small"
                        sx={glassmorphicTextFieldSx}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{
                            min: formatDateForInput(startDate),
                        }}
                        placeholder="(optionnel)"
                    />
                </Box>
            </Box>

            {/* ── Sticky Bottom Button ── */}
            <Box
                sx={{
                    position: "fixed",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    px: 2,
                    py: 2,
                    background: "rgba(255,255,255,0.88)",
                    backdropFilter: "blur(24px) saturate(1.4)",
                    WebkitBackdropFilter: "blur(24px) saturate(1.4)",
                    borderTop: "1px solid rgba(255,255,255,0.5)",
                    boxShadow: "0 -4px 20px rgba(0,0,0,0.06)",
                    zIndex: 10,
                }}
            >
                <Button
                    fullWidth
                    variant="contained"
                    onClick={handleAddMembers}
                    sx={{
                        minHeight: 52,
                        borderRadius: "14px",
                        fontWeight: 700,
                        fontSize: "1rem",
                        textTransform: "none",
                        background: "linear-gradient(135deg, #FF9015 0%, #FFC100 100%)",
                        boxShadow: "0 4px 16px rgba(255,144,21,0.35), 0 1px 3px rgba(255,144,21,0.2)",
                        "&:hover": {
                            background: "linear-gradient(135deg, #FFC100 0%, #FF9015 100%)",
                        },
                        "&:active": {
                            transform: "scale(0.97)",
                        },
                    }}
                >
                    <Langs str="ADD" />
                </Button>
            </Box>
        </Dialog>
    );
}
